import React, { useEffect, useState } from 'react';

/**
 * The shared voice-mode pattern — voice is an interface layer over the same
 * flows, never a separate product. Any bottom modal can opt in:
 *
 *   - the Sunny avatar in the modal header is the toggle (BottomModal's
 *     onBuddyTap / buddyActive props)
 *   - Sunny reads the screen aloud, then listens
 *   - the user answers by speaking; everything sayable is also tappable
 *
 * First used by the Journey (card-flows §8), reused by Care Plans (§3 —
 * emergency steps read aloud step by step).
 */

export type VoiceActivity = 'idle' | 'speaking' | 'listening';

/* mock narration timing — Sunny "reads" for a beat, then listens */
export function useReadThenListen(voiceMode: boolean, onVoiceActivity: ((a: VoiceActivity) => void) | undefined, readMs: number) {
  const [phase, setPhase] = useState<'reading' | 'listening'>('reading');
  useEffect(() => {
    if (!voiceMode) {
      onVoiceActivity?.('idle');
      return;
    }
    setPhase('reading');
    onVoiceActivity?.('speaking');
    const t = setTimeout(() => {
      setPhase('listening');
      onVoiceActivity?.('listening');
    }, readMs);
    return () => clearTimeout(t);
  }, [voiceMode]);
  return phase;
}

/* the voice-mode bar — shows whether Sunny is reading or listening, and the
   phrases the user can say. Tapping a quoted phrase simulates saying it. */
export function VoiceBar({
  mode,
  hint,
  sayables
}: {
  mode: 'reading' | 'listening';
  hint: string;
  sayables?: {
    label: string;
    onSay: () => void;
  }[];
}) {
  return <div className="dhp-fade-up mt-4 rounded-card border border-[#17695E2b] bg-teal-soft p-3.5" data-id="element-892">
      <div className="flex items-center gap-2.5" data-id="element-893">
        {mode === 'reading' ? <div className="flex h-4 shrink-0 items-center gap-[3px]" data-id="element-894">
            {[0, 1, 2, 3, 4].map(i => <span key={i} className="dhp-wave-bar w-[3px] rounded-full bg-teal" style={{
          height: '100%',
          animationDelay: `${i * 0.12}s`
        }} data-id="element-895" />)}
          </div> : <span className="relative flex h-2.5 w-2.5 shrink-0" data-id="element-896">
            <span className="dhp-ring absolute inline-flex h-full w-full rounded-full bg-teal" data-id="element-897" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-teal" data-id="element-898" />
          </span>}
        <span className="text-[12.5px] font-bold leading-snug text-teal-deep" data-id="element-899">{hint}</span>
      </div>

      {mode === 'listening' && sayables && sayables.length > 0 && <>
          <div className="mt-2.5 flex flex-wrap gap-2" data-id="element-900">
            {sayables.map(s => <button key={s.label} onClick={s.onSay} className="rounded-full border border-dashed border-teal bg-cream px-3.5 py-2 text-[12.5px] font-semibold text-teal-deep transition-all hover:-translate-y-0.5 active:scale-95" data-id="element-901">
                “{s.label}”
              </button>)}
          </div>
          <p className="mt-2 text-[10.5px] font-medium text-ink-faint" data-id="element-902">In the prototype, tap a phrase to simulate saying it.</p>
        </>}
    </div>;
}